import React, { useEffect, useState } from 'react';
import { useHRMS } from '../context/HRMSContext.jsx';
import PermissionGuard from '../components/PermissionGuard.jsx';
import { MODULES, ACTIONS } from '../context/PermissionsContext.jsx';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function Dashboard() {
  const { state, loadLeaves, loadWorkers, loadShifts } = useHRMS();
  const [chartFilter, setChartFilter] = useState('1Y');

  useEffect(() => {
    const load = async () => {
      try {
        await loadWorkers();
        await loadShifts();
        if (state.leaves.length === 0) await loadLeaves();
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, []);

  const getTodayDate = () => new Date().toISOString().split('T')[0];

  const totalWorkers    = Array.isArray(state.workers) ? state.workers.length : 0;
  const presentToday    = Array.isArray(state.attendance)
    ? state.attendance.filter(a => a.status === 0 && (a.date ? new Date(a.date).toISOString().split('T')[0] : '') === getTodayDate()).length
    : 0;
  const pendingLeaves   = Array.isArray(state.leaves)
    ? state.leaves.filter(l => l.leaveStatus === 0 || l.leaveStatus === null || l.leaveStatus === undefined).length
    : 0;
  const totalDepartments = Array.isArray(state.workers)
    ? [...new Set(state.workers.map(w => w.department))].length
    : 0;

  // Build monthly chart data from workers joinDate
  const chartData = MONTHS.map((month, i) => {
    const workers = Array.isArray(state.workers)
      ? state.workers.filter(w => w.joinDate && new Date(w.joinDate).getMonth() === i).length
      : 0;
    const leaves = Array.isArray(state.leaves)
      ? state.leaves.filter(l => l.startDt && new Date(l.startDt).getMonth() === i).length
      : 0;
    return { month, Workers: workers, Leaves: leaves };
  });

  const filterMonths = { '1M': 1, '6M': 6, '1Y': 12, 'ALL': 12 };
  const visibleData = chartData.slice(12 - (filterMonths[chartFilter] || 12));

  const getShiftName = (shiftId) => {
    if (!shiftId || !Array.isArray(state.shifts)) return '-';
    const s = state.shifts.find(s => s.id === shiftId);
    return s ? s.shiftName : '-';
  };

  const statCards = [
    {
      label: 'Total Workers',
      value: totalWorkers,
      icon: 'bi-people-fill',
      trend: '+2.3%',
      trendUp: true,
      sub: 'This Month',
      module: MODULES.DASHBOARD.submodules?.TOTAL_WORKERS || 'total_workers',
    },
    {
      label: 'Present Today',
      value: presentToday,
      icon: 'bi-calendar-check-fill',
      trend: '+8.1%',
      trendUp: true,
      sub: 'Last Week',
      module: MODULES.DASHBOARD.submodules?.PRESENT_TODAY || 'present_today',
    },
    {
      label: 'Pending Leaves',
      value: pendingLeaves,
      icon: 'bi-calendar-x-fill',
      trend: '-0.3%',
      trendUp: false,
      sub: 'Last Month',
      module: MODULES.DASHBOARD.submodules?.PENDING_LEAVES || 'pending_leaves',
    },
    {
      label: 'Departments',
      value: totalDepartments,
      icon: 'bi-building-fill',
      trend: '-10.6%',
      trendUp: false,
      sub: 'Last Month',
      module: 'departments',
    },
  ];

  return (
    <PermissionGuard module={MODULES.DASHBOARD} action={ACTIONS.LIST}>
      <div className="dash-wrap">
        <p className="dash-welcome">WELCOME HRMS!</p>

        <div className="dash-grid">
          {/* LEFT: 2x2 stat cards */}
          <div className="dash-cards">
            {statCards.map((card, idx) => (
              <PermissionGuard key={idx} module={MODULES.DASHBOARD} action={ACTIONS.VIEW} submodule={card.module}>
                <div className="dash-stat-card">
                  <div className="dsc-top">
                    <div className="dsc-icon">
                      <i className={`bi ${card.icon}`}></i>
                    </div>
                    <div className="dsc-info">
                      <div className="dsc-label">{card.label}</div>
                      <div className="dsc-value">{card.value.toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="dsc-bottom">
                    <span className={`dsc-trend ${card.trendUp ? 'up' : 'down'}`}>
                      <i className={`bi ${card.trendUp ? 'bi-arrow-up' : 'bi-arrow-down'}`}></i>
                      {card.trend}
                    </span>
                    <span className="dsc-sub">{card.sub}</span>
                    <span className="dsc-view">View More</span>
                  </div>
                </div>
              </PermissionGuard>
            ))}
          </div>

          {/* RIGHT: Chart */}
          <PermissionGuard module={MODULES.DASHBOARD} action={ACTIONS.VIEW} submodule="recent_workers">
            <div className="dash-chart-card">
              <div className="dcc-header">
                <span className="dcc-title">Performance</span>
                <div className="dcc-filters">
                  {['ALL','1M','6M','1Y'].map(f => (
                    <button
                      key={f}
                      className={`dcc-filter-btn ${chartFilter === f ? 'active' : ''}`}
                      onClick={() => setChartFilter(f)}
                    >{f}</button>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart data={visibleData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Legend iconType="circle" iconSize={8} />
                  <Bar dataKey="Workers" fill="#4f46e5" radius={[4,4,0,0]} barSize={22} />
                  <Line dataKey="Leaves" type="monotone" stroke="#10b981" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </PermissionGuard>
        </div>

        {/* Bottom tables - 3 columns */}
        <div className="dash-tables">
          <PermissionGuard module={MODULES.DASHBOARD} action={ACTIONS.VIEW} submodule="recent_workers">
            <div className="dash-table-card">
              <div className="dtc-header">
                <i className="bi bi-people-fill"></i> Recent Workers
              </div>
              <table className="table table-sm dash-table">
                <thead>
                  <tr><th>Name</th><th>Department</th><th>Shift</th><th>Join Date</th></tr>
                </thead>
                <tbody>
                  {Array.isArray(state.workers) && state.workers.length > 0
                    ? state.workers.slice(-5).map(w => (
                      <tr key={w.id}>
                        <td>{w.name}</td>
                        <td>{w.department}</td>
                        <td>{getShiftName(w.shiftId)}</td>
                        <td>{w.joinDate}</td>
                      </tr>
                    ))
                    : <tr><td colSpan="4" className="text-center text-muted">No workers found</td></tr>
                  }
                </tbody>
              </table>
            </div>
          </PermissionGuard>

          <PermissionGuard module={MODULES.DASHBOARD} action={ACTIONS.VIEW} submodule="pending_leave_requests">
            <div className="dash-table-card">
              <div className="dtc-header">
                <i className="bi bi-calendar-x-fill"></i> Pending Leave Requests
              </div>
              <table className="table table-sm dash-table">
                <thead>
                  <tr><th>Worker</th><th>Type</th><th>From</th></tr>
                </thead>
                <tbody>
                  {Array.isArray(state.leaves) && state.leaves.filter(l =>
                    l.leaveStatus === 0 || l.leaveStatus === null || l.leaveStatus === undefined
                  ).length > 0
                    ? state.leaves.filter(l =>
                        l.leaveStatus === 0 || l.leaveStatus === null || l.leaveStatus === undefined
                      ).slice(0, 5).map(l => {
                        const w = Array.isArray(state.workers) ? state.workers.find(w => w.id === l.workerId) : null;
                        return (
                          <tr key={l.id}>
                            <td>{w?.name || `Worker ${l.workerId}`}</td>
                            <td>{l.type}</td>
                            <td>{l.startDt}</td>
                          </tr>
                        );
                      })
                    : <tr><td colSpan="3" className="text-center text-muted">No pending leaves</td></tr>
                  }
                </tbody>
              </table>
            </div>
          </PermissionGuard>

          <PermissionGuard module={MODULES.DASHBOARD} action={ACTIONS.VIEW} submodule={MODULES.DASHBOARD.submodules?.PRESENT_TODAY || 'present_today'}>
            <div className="dash-table-card">
              <div className="dtc-header">
                <i className="bi bi-clock-history"></i> Today's Attendance
              </div>
              <table className="table table-sm dash-table">
                <thead>
                  <tr><th>Worker</th><th>Department</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {Array.isArray(state.attendance) && state.attendance.filter(a =>
                    (a.date ? new Date(a.date).toISOString().split('T')[0] : '') === getTodayDate()
                  ).length > 0
                    ? state.attendance.filter(a =>
                        (a.date ? new Date(a.date).toISOString().split('T')[0] : '') === getTodayDate()
                      ).slice(0, 5).map(a => {
                        const w = Array.isArray(state.workers) ? state.workers.find(w => w.id === a.workerId) : null;
                        return (
                          <tr key={a.id}>
                            <td>{w?.name || `Worker ${a.workerId}`}</td>
                            <td>{w?.department || '-'}</td>
                            <td>
                              <span className={`att-badge ${a.status === 0 ? 'present' : 'absent'}`}>
                                {a.status === 0 ? 'Present' : 'Absent'}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    : <tr><td colSpan="3" className="text-center text-muted">No attendance today</td></tr>
                  }
                </tbody>
              </table>
            </div>
          </PermissionGuard>
        </div>
      </div>
    </PermissionGuard>
  );
}

export default Dashboard;
